import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import {
  UploadCloud, FileCheck2, X, Loader2, CheckCircle2, ArrowUpRight, MessageCircle, ShieldCheck, Eye,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { resolveSite, materials as staticMaterials } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { Link } from "react-router-dom";
import ModelViewer from "../components/ModelViewer";
import AIAnalysisPanel from "../components/AIAnalysisPanel";
import PriceEstimateCard from "../components/PriceEstimateCard";
import { buildAnalysis } from "../lib/modelAnalysis";
import { API_BASE } from "../lib/env";
import { pub } from "../lib/pubThemeClasses";
import { useLanguage } from "../context/LanguageContext";
import { pickMaterialDisplay } from "../lib/cmsI18n";
import {
  trackQuoteUpload,
  trackQuoteAnalysis,
  trackQuoteReprice,
  trackQuoteSubmit,
  trackWhatsappClick,
  trackQuoteCTAClick,
  track3mfSettingsDetected,
} from "../lib/tracking";

const ALLOWED = [".stl", ".obj", ".step", ".stp", ".3mf"];

/** macOS/WebKit sıkça .stl seçicide gizliyor; b/k uyumlu uzantı + sık MIME türleri (yine de ALLOWED doğrular) */
const MODEL_ACCEPT_ATTR =
  ".stl,.STL,.obj,.OBJ,.step,.STEP,.stp,.STP,.3mf,.3MF,model/stl,model/obj,model/step,application/sla,application/vnd.ms-pki.stl,application/vnd.ms-package.3dmanufacturing-3dmodel+xml,application/zip,text/plain,text/x-obj";

const INFILL_OPTIONS = [10, 15, 20, 30];
const WALL_OPTIONS = [2, 3, 4];
const LAYER_HEIGHT_OPTIONS = [0.28, 0.2, 0.16, 0.12];
const QUALITY_OPTIONS = ["Ekonomik", "Standart", "Dayanıklı", "Detaylı"];

/** Sunucudaki tahmin için geçerli malzeme anahtarı (NYLON vb. dahil). */
function engineMaterial(mat) {
  if (!mat || mat === "ONERI") return "PLA";
  const allowed = ["PLA", "PETG", "ABS", "TPU", "RESIN", "NYLON"];
  return allowed.includes(mat) ? mat : "PLA";
}

/** Fiyat / reprice eşiti için tutarlı imza */
function serializePrintFingerprint(material, quantity, cfg) {
  return JSON.stringify({
    material: engineMaterial(material),
    quantity: Math.max(1, parseInt(String(quantity || 1), 10)),
    infill_percent: cfg.infillPercent,
    wall_count: cfg.wallCount,
    layer_height_mm: cfg.layerHeightMm,
    quality_profile: cfg.qualityProfile,
  });
}

function appendAnalyzeFormFields(fd, { material, quantity, cfg }) {
  fd.append("material", engineMaterial(material));
  fd.append("quantity", String(Math.max(1, parseInt(String(quantity || 1), 10))));
  fd.append("infill_percent", String(cfg.infillPercent));
  fd.append("wall_count", String(cfg.wallCount));
  fd.append("layer_height_mm", String(cfg.layerHeightMm));
  fd.append("quality_profile", cfg.qualityProfile);
}

/** 3MF’den gelen snapshot ile güncel formun aynı mı (sayısal toleransla katman) */
function printCfgsEqual(a, b) {
  if (!a || !b) return false;
  return (
    a.infillPercent === b.infillPercent &&
    a.wallCount === b.wallCount &&
    Math.abs(Number(a.layerHeightMm) - Number(b.layerHeightMm)) < 1e-6 &&
    a.qualityProfile === b.qualityProfile
  );
}

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/** İletişim / uzun metin */
const sansInputCls = `${pub.input} ${pub.inputFocus} min-w-0 max-w-full font-sans text-sm h-12 px-4 rounded-sm transition-colors`;

/** Baskı parametreleri — mono */
const printSelectCls = `${pub.input} ${pub.inputFocus} min-w-0 max-w-full font-mono text-sm h-12 px-4 rounded-sm transition-colors tabular-nums`;

const selectOptionCls = "bg-white dark:bg-[#111216]";

export default function Quote() {
  const { settings, materials: liveMaterials } = usePublicData();
  const siteConfig = resolveSite(settings);
  const materials = liveMaterials?.length ? liveMaterials : staticMaterials;
  const { t, language } = useLanguage();

  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Sunucu / gönderim hatası — ekran okuyucu için role="alert" metni */
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [rawMeta, setRawMeta] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [backendEstimate, setBackendEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateFallbackReason, setEstimateFallbackReason] = useState("no-file");
  const analyzeReqRef = useRef(0);
  const repriceReqRef = useRef(0);
  /** Son başarılı analyze/reprice ile aynı baskı imzası — gereksiz reprice önlemek için */
  const syncedPrintFingerprintRef = useRef("");
  const priceToastSentRef = useRef(false);
  /** Aynı dosya için `3mf_settings_detected` yalnızca bir kez */
  const tracked3mfFingerprintRef = useRef("");
  const fileRef = useRef(null);

  /** 3MF analizinden okunan görüntülenecek değerler (referans için tutulur) */
  const [detected3mfSettings, setDetected3mfSettings] = useState(null);
  /** 3MF ilk uygulanıncaki baskı imzası — manuel sapma tespiti */
  const [applied3mfPrintCfgSnapshot, setApplied3mfPrintCfgSnapshot] = useState(null);

  const [printCfg, setPrintCfg] = useState({
    infillPercent: 15,
    wallCount: 2,
    layerHeightMm: 0.2,
    qualityProfile: "Standart",
  });

  const userOverrode3mfSettings = useMemo(
    () =>
      !!(detected3mfSettings &&
        applied3mfPrintCfgSnapshot &&
        !printCfgsEqual(printCfg, applied3mfPrintCfgSnapshot)),
    [detected3mfSettings, applied3mfPrintCfgSnapshot, printCfg],
  );

  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    material: "PLA",
    quantity: 1,
    deadline: "",
    notes: "",
  });

  useEffect(() => {
    if (window.location.hash === "#teklif-yukle") {
      document.getElementById("teklif-yukle")?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (!file) {
      priceToastSentRef.current = false;
      return;
    }
    if (estimateLoading) return;
    if (backendEstimate?.pricing != null && typeof backendEstimate.pricing.low === "number") {
      if (!priceToastSentRef.current) {
        toast.success(t("toast.priceReady"));
        priceToastSentRef.current = true;
      }
    }
  }, [file, estimateLoading, backendEstimate?.pricing, t]);

  const updateField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onFile = (f) => {
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error(`${t("toast.unsupportedFormat")} ${ALLOWED.join(", ")}`);
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error(t("toast.fileTooBig"));
      return;
    }
    setFile(f);
    priceToastSentRef.current = false;
    trackQuoteUpload(f, { language });
    setDetected3mfSettings(null);
    setApplied3mfPrintCfgSnapshot(null);
    tracked3mfFingerprintRef.current = "";
    setAnalysis(null); // reset; viewer will call back with fresh metadata
    setRawMeta(null);
    setAnalysisId(null);
    setBackendEstimate(null);
    setEstimateFallbackReason("analyzing");
    syncedPrintFingerprintRef.current = "";
  };

  const handleAnalysis = useCallback((meta) => {
    setRawMeta(meta);
    setAnalysis(buildAnalysis(meta));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("toast.needNamePhone"));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("infill_percent", String(printCfg.infillPercent));
      fd.append("wall_count", String(printCfg.wallCount));
      fd.append("layer_height_mm", String(printCfg.layerHeightMm));
      fd.append("quality_profile", printCfg.qualityProfile);
      if (analysisId) fd.append("analysis_id", analysisId);
      if (file) fd.append("file", file);
      await axios.post(`${API_BASE}/quote-requests`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      trackQuoteSubmit(form, backendEstimate, printCfg, { language });
      toast.success(t("toast.requestReceived"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d?.detail ?? String(d?.loc ?? "")).filter(Boolean).join(" ") || undefined
            : detail?.msg;
      msg = msg || t("toast.errorGeneric");
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fileFingerprint = file ? `${file.name}:${file.size}:${file.lastModified}` : "";

  /** STL/OBJ yüklendiğinde tek multipart analyze (dosya yenilendiğinde) */
  useEffect(() => {
    const runAnalyze = async () => {
      if (!file) {
        setEstimateLoading(false);
        setAnalysisId(null);
        setBackendEstimate(null);
        syncedPrintFingerprintRef.current = "";
        setDetected3mfSettings(null);
        setApplied3mfPrintCfgSnapshot(null);
        tracked3mfFingerprintRef.current = "";
        setEstimateFallbackReason("no-file");
        return;
      }

      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      if (ext === ".step" || ext === ".stp") {
        setEstimateLoading(false);
        setAnalysisId(null);
        setBackendEstimate(null);
        syncedPrintFingerprintRef.current = "";
        setEstimateFallbackReason("pending-review");
        return;
      }

      const reqId = ++analyzeReqRef.current;
      setEstimateLoading(true);
      setEstimateFallbackReason("analyzing");

      try {
        const fd = new FormData();
        fd.append("file", file);
        appendAnalyzeFormFields(fd, { material: form.material, quantity: form.quantity, cfg: printCfg });

        const { data } = await axios.post(`${API_BASE}/quote/analyze`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (analyzeReqRef.current !== reqId) return;
        setAnalysisId(data.analysis_id || null);
        setBackendEstimate(data);
        trackQuoteAnalysis(data, { language });
        setEstimateFallbackReason(
          data.pricing_status === "pending_review"
            ? "pending-review"
            : data.pricing && typeof data.pricing.low === "number" && typeof data.pricing.high === "number"
              ? "none"
              : "technical-review",
        );
        // Apply BambuLab print settings — update form AND mark as synced to skip reprice
        if (data.bambu_print_settings) {
          const bs = data.bambu_print_settings;
          const newCfg = {
            infillPercent: bs.infill_percent ?? printCfg.infillPercent,
            wallCount: bs.wall_count ?? printCfg.wallCount,
            layerHeightMm: bs.layer_height_mm ?? printCfg.layerHeightMm,
            qualityProfile: bs.quality_profile ?? printCfg.qualityProfile,
          };
          syncedPrintFingerprintRef.current = serializePrintFingerprint(form.material, form.quantity, newCfg);
          setPrintCfg(newCfg);
          setDetected3mfSettings({
            infill_percent: bs.infill_percent,
            wall_count: bs.wall_count,
            layer_height_mm: bs.layer_height_mm,
            ...(bs.quality_profile != null && String(bs.quality_profile).trim() !== ""
              ? { quality_profile: bs.quality_profile }
              : {}),
          });
          setApplied3mfPrintCfgSnapshot({ ...newCfg });
          if (tracked3mfFingerprintRef.current !== fileFingerprint) {
            track3mfSettingsDetected({
              infill_percent: bs.infill_percent,
              wall_count: bs.wall_count,
              layer_height_mm: bs.layer_height_mm,
              quality_profile: bs.quality_profile ?? "",
              language,
            });
            tracked3mfFingerprintRef.current = fileFingerprint;
          }
          toast.info(t("quote.3mf.settingsApplied"));
        } else {
          setDetected3mfSettings(null);
          setApplied3mfPrintCfgSnapshot(null);
          const fp = serializePrintFingerprint(form.material, form.quantity, printCfg);
          syncedPrintFingerprintRef.current = fp;
        }
      } catch {
        if (analyzeReqRef.current !== reqId) return;
        setAnalysisId(null);
        setBackendEstimate(null);
        syncedPrintFingerprintRef.current = "";
        setDetected3mfSettings(null);
        setApplied3mfPrintCfgSnapshot(null);
        setEstimateFallbackReason("technical-review");
      } finally {
        if (analyzeReqRef.current === reqId) setEstimateLoading(false);
      }
    };
    runAnalyze();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- multipart yalnızca dosya (fileFingerprint) değişince; ayar reprice ile
  }, [fileFingerprint]);

  /** Aynı analiz için baskı ayarı değişince dosyayı tekrar yüklemeden fiyat güncelle */
  useEffect(() => {
    if (!file || !analysisId) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (ext === ".step" || ext === ".stp") return;

    const hasServerPricing =
      backendEstimate &&
      typeof backendEstimate.pricing?.low === "number" &&
      typeof backendEstimate.pricing?.high === "number";
    const fp = serializePrintFingerprint(form.material, form.quantity, printCfg);
    if (!hasServerPricing) {
      syncedPrintFingerprintRef.current = fp;
      return;
    }

    if (fp === syncedPrintFingerprintRef.current) return;

    const reqId = ++repriceReqRef.current;
    let cancelled = false;

    (async () => {
      try {
        setEstimateLoading(true);
        const qty = Math.max(1, parseInt(String(form.quantity || 1), 10));
        const { data } = await axios.post(`${API_BASE}/quote/reprice`, {
          analysis_id: analysisId,
          material: engineMaterial(form.material),
          quantity: qty,
          infill_percent: printCfg.infillPercent,
          wall_count: printCfg.wallCount,
          layer_height_mm: printCfg.layerHeightMm,
          quality_profile: printCfg.qualityProfile,
        });
        if (cancelled || repriceReqRef.current !== reqId) return;
        syncedPrintFingerprintRef.current = fp;
        setBackendEstimate((prev) => ({
          ...(prev || {}),
          analysis_id: analysisId,
          mass_estimate_g: data.mass_estimate_g,
          print_time_estimate_h: data.print_time_estimate_h,
          support_risk: data.support_risk,
          pricing_status: prev?.pricing_status,
          geometry: prev?.geometry,
          pricing: data.pricing,
          customer_print_settings: data.customer_print_settings,
          pricing_settings_snapshot: data.pricing_settings_snapshot,
        }));
        trackQuoteReprice(data, printCfg, { language });
        setEstimateFallbackReason(data.pricing ? "none" : "technical-review");
      } catch {
        if (cancelled || repriceReqRef.current !== reqId) return;
        syncedPrintFingerprintRef.current = "";
        toast.error(t("toast.priceUpdateFailed"));
      } finally {
        if (!cancelled && repriceReqRef.current === reqId) setEstimateLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `file` fileFingerprint ile; printCfg bileşeni primitive alanlarla izleniyor
  }, [
    fileFingerprint,
    analysisId,
    backendEstimate,
    form.material,
    form.quantity,
    printCfg.infillPercent,
    printCfg.wallCount,
    printCfg.layerHeightMm,
    printCfg.qualityProfile,
  ]);

  if (done) {
    return (
      <section className="min-h-[70vh] grid place-items-center px-6 py-20 bg-site" aria-live="polite" role="status">
        <div className={`max-w-2xl w-full p-10 md:p-14 text-center ${pub.cardElevated}`}>
          <div className="w-16 h-16 border border-[#00FF9D]/50 bg-[#00FF9D]/10 mx-auto grid place-items-center mb-8">
            <CheckCircle2 className="w-8 h-8 text-[#00FF9D]" />
          </div>
          <h1 className={`font-sans text-3xl md:text-5xl font-bold tracking-tight mb-3 ${pub.heroHeading}`}>
            {t("success.title")}
          </h1>
          <p className={`${pub.muted} mb-8 max-w-lg mx-auto text-sm leading-relaxed`}>{t("success.subtitle")}</p>
          <div className={`pt-6 border-t ${pub.borderSubtle}`}>
            <p className={`text-sm font-medium mb-4 ${pub.muted}`}>{t("whatsapp.title")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                data-testid="quote-success-home"
                className={`inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-xs px-6 py-4 ${pub.btnGhost}`}
              >
                {t("quote.success.home")}
              </Link>
              <a
                href={siteConfig.whatsappLink(t("quote.success.wa.intro"))}
                target="_blank"
                rel="noreferrer"
                data-testid="quote-success-whatsapp"
                onClick={() => trackWhatsappClick("quote-success", { language })}
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-black font-bold uppercase tracking-wider text-xs px-6 py-4"
              >
                {t("whatsapp.cta")}
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className={`relative border-b bg-grain ${pub.borderSubtle}`}>
        <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#FF4F00]/10 blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF4F00] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FF4F00]" /> {t("quote.hero.eyebrow")}
          </div>
          <h1 className={`font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.9] max-w-4xl ${pub.heroHeading}`}>
            {t("quote.hero.line1")}
            <br />
            <span className="text-[#FF4F00]">{t("quote.hero.highlight")} </span>
            {t("quote.hero.line2")}
          </h1>
          <p className={`font-sans mt-6 text-lg max-w-2xl ${pub.muted}`}>{t("quote.hero.sub")}</p>
        </div>
      </section>

      <section className={`border-b ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24">
          <form onSubmit={submit} className="space-y-10" data-testid="quote-form">

            {/* ==== TOP: Upload (left)  +  Viewer + AI (right) ==== */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

              {/* Upload column */}
              <div id="teklif-yukle" className="lg:col-span-5 space-y-6">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    onFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={() => fileRef.current?.click()}
                  data-testid="quote-file-dropzone"
                  className={`max-w-full min-w-0 border-2 border-dashed p-10 md:p-12 text-center cursor-pointer transition-colors relative rounded-sm ${
                    dragOver
                      ? "border-orange-400 bg-orange-50/70 shadow-md dark:border-orange-400/70 dark:bg-orange-500/10"
                      : pub.dashedUpload
                  }`}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept={MODEL_ACCEPT_ATTR}
                    className="hidden"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    data-testid="quote-file-input"
                  />
                  {!file ? (
                    <>
                      <UploadCloud className="w-10 h-10 mx-auto text-[#FF4F00] mb-6" strokeWidth={1.5} />
                      <div className={`font-sans text-xl md:text-2xl font-semibold tracking-tight mb-1.5 ${pub.heroHeading}`}>
                        {t("quote.upload.title")}
                      </div>
                      <div className={`font-sans text-sm mb-1 ${pub.muted}`}>{t("quote.upload.sub")}</div>
                      <div className={`font-sans text-xs mb-6 ${pub.mutedStr}`}>{t("quote.upload.hint")}</div>
                      <div className={`inline-flex items-center gap-3 font-mono text-xs uppercase tracking-wide px-3 py-1.5 rounded-sm border ${pub.borderSubtle} bg-white/80 dark:bg-transparent`}>
                        <span className="text-[#FF4F00]">STL</span>
                        <span>·</span>
                        <span className="text-[#FF4F00]">OBJ</span>
                        <span>·</span>
                        <span className="text-[#FF4F00]">STEP</span>
                        <span>·</span>
                        <span>{t("quote.upload.badges.max")}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-4 text-left">
                      <FileCheck2 className="w-10 h-10 text-[#00FF9D] shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-sans font-semibold truncate ${pub.heroHeading}`}>{file.name}</div>
                        <div className={`font-mono text-xs uppercase tracking-wide mt-1 ${pub.mutedStr}`}>
                          {formatSize(file.size)} · {t("quote.uploaded")}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          syncedPrintFingerprintRef.current = "";
                          setFile(null);
                          setAnalysis(null);
                          setRawMeta(null);
                          setAnalysisId(null);
                          setBackendEstimate(null);
                          setEstimateFallbackReason("no-file");
                        }}
                        data-testid="quote-file-remove"
                        className={`p-2 border rounded-sm transition-colors hover:border-orange-400/80 dark:hover:border-orange-400/50 ${pub.borderStrongTone}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-sm px-4 py-3 text-sm font-sans leading-relaxed space-y-1.5 ${pub.softPanel} ${pub.muted}`}
                  data-testid="quote-format-expectations"
                >
                  <p>
                    <span className="font-mono font-semibold text-[#FF4F00]">STL</span> → {t("quote.expect.stlRest")}
                  </p>
                  <p>
                    <span className="font-mono font-semibold text-[#FF4F00]">OBJ</span> → {t("quote.expect.objRest")}
                  </p>
                  <p>
                    <span className="font-mono font-semibold text-[#FF4F00]">STEP</span> → {t("quote.expect.stepRest")}
                  </p>
                  <p>
                    <span className="font-mono font-semibold text-[#FF4F00]">3MF</span> → {t("quote.expect.3mfRest")}
                  </p>
                </div>

                <div className={`p-6 space-y-5 rounded-sm border shadow-sm dark:shadow-none ${pub.borderSubtle} ${pub.softPanel}`} data-testid="quote-print-settings">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 border border-[#FF4F00]/40 bg-[#FF4F00]/5 grid place-items-center shrink-0">
                      <SlidersHorizontal className="w-5 h-5 text-[#FF4F00]" strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                      <div className={`font-sans font-semibold text-base tracking-tight ${pub.heroHeading}`}>{t("quote.print.title")}</div>
                      <p className={`font-sans text-xs mt-2 leading-relaxed ${pub.muted}`}>{t("quote.print.description")}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className={`font-mono text-xs uppercase tracking-[0.15em] mb-2 block ${pub.mutedStr}`}>{t("quote.label.material")}</span>
                      <select
                        value={form.material}
                        onChange={(e) => updateField("material", e.target.value)}
                        data-testid="quote-print-material"
                        className={printSelectCls + " appearance-none"}
                      >
                        {materials.map((m) => (
                          <option key={m.key} value={m.key} className={selectOptionCls}>{pickMaterialDisplay(m, language).name}</option>
                        ))}
                        <option value="ONERI" className={selectOptionCls}>{t("quote.materialSuggest")}</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className={`font-mono text-xs uppercase tracking-[0.15em] mb-2 block ${pub.mutedStr}`}>{t("quote.label.infill")}</span>
                      <select
                        value={printCfg.infillPercent}
                        onChange={(e) => setPrintCfg((s) => ({ ...s, infillPercent: parseInt(e.target.value, 10) }))}
                        data-testid="quote-print-infill"
                        className={printSelectCls + " appearance-none"}
                      >
                        {INFILL_OPTIONS.map((n) => (
                          <option key={n} value={n} className={selectOptionCls}>%{n}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className={`font-mono text-xs uppercase tracking-[0.15em] mb-2 block ${pub.mutedStr}`}>{t("quote.label.walls")}</span>
                      <select
                        value={printCfg.wallCount}
                        onChange={(e) => setPrintCfg((s) => ({ ...s, wallCount: parseInt(e.target.value, 10) }))}
                        data-testid="quote-print-walls"
                        className={printSelectCls + " appearance-none"}
                      >
                        {WALL_OPTIONS.map((n) => (
                          <option key={n} value={n} className={selectOptionCls}>{n}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className={`font-mono text-xs uppercase tracking-[0.15em] mb-2 block ${pub.mutedStr}`}>{t("quote.label.layer")}</span>
                      <select
                        value={printCfg.layerHeightMm}
                        onChange={(e) => setPrintCfg((s) => ({ ...s, layerHeightMm: parseFloat(e.target.value) }))}
                        data-testid="quote-print-layer"
                        className={printSelectCls + " appearance-none"}
                      >
                        {LAYER_HEIGHT_OPTIONS.map((h) => (
                          <option key={h} value={h} className={selectOptionCls}>{h.toFixed(2)} mm</option>
                        ))}
                      </select>
                    </label>
                    <label className="block sm:col-span-2">
                      <span className={`font-mono text-xs uppercase tracking-[0.15em] mb-2 block ${pub.mutedStr}`}>{t("quote.label.quality")}</span>
                      <select
                        value={printCfg.qualityProfile}
                        onChange={(e) => setPrintCfg((s) => ({ ...s, qualityProfile: e.target.value }))}
                        data-testid="quote-print-quality"
                        className={printSelectCls + " appearance-none"}
                      >
                        {QUALITY_OPTIONS.map((q) => (
                          <option key={q} value={q} className={selectOptionCls}>
                            {t(`quote.quality.${q}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                {/* Trust note */}
                <div className={`px-5 py-4 flex items-start gap-3 rounded-sm border ${pub.softPanel}`}>
                  <ShieldCheck className="w-4 h-4 text-[#00FF9D] mt-0.5 shrink-0" />
                  <p className={`font-sans text-sm leading-relaxed ${pub.muted}`}>{t("quote.trust")}</p>
                </div>

                {/* Quick stats strip */}
                <div className={`rounded-sm shadow-sm dark:shadow-none p-6 space-y-3 font-sans text-sm ${pub.card}`}>
                  <div className="flex justify-between">
                    <span>{t("quote.stats.response")}</span>
                    <span className={`font-medium ${pub.heroHeading}`}>{t("quote.stats.responseVal")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("quote.stats.start")}</span>
                    <span className={`font-medium ${pub.heroHeading}`}>{t("quote.stats.sameDay")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("quote.stats.delivery")}</span>
                    <span className={`font-medium ${pub.heroHeading}`}>{t("quote.stats.deliveryVal")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("quote.stats.privacy")}</span>
                    <span className="text-[#00FF9D]">{t("quote.stats.nds")}</span>
                  </div>
                </div>
              </div>

              {/* Viewer + AI column */}
              <div className="lg:col-span-7 space-y-5">
                <div className={`flex items-center gap-3 text-sm font-sans ${pub.muted}`}>
                  <Eye className="w-4 h-4 text-[#FF4F00]" />
                  <span>{t("quote.preview.hint")}</span>
                </div>

                <ModelViewer file={file} onAnalysis={handleAnalysis} />

                {analysis && <AIAnalysisPanel analysis={analysis} />}

                <PriceEstimateCard
                  backendEstimate={backendEstimate}
                  loading={estimateLoading}
                  fallbackReason={estimateFallbackReason}
                  ctaScrollTargetId="quote-contact-form"
                  detected3mfSettings={detected3mfSettings}
                  userOverrode3mfSettings={userOverrode3mfSettings}
                  quoteFileExt={file ? `.${(file.name.split(".").pop() || "").toLowerCase()}` : null}
                />
              </div>
            </div>

            {/* Funnel: görünür fiyat → form */}
            {file && backendEstimate?.pricing != null && !estimateLoading ? (
              <div
                className={`my-12 md:my-16 p-8 md:p-10 rounded-sm border text-center shadow-sm dark:shadow-none ${pub.softPanel}`}
                data-testid="quote-funnel-banner"
              >
                <h2 className={`font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4 ${pub.heroHeading}`}>{t("quote.funnel.title")}</h2>
                <p className={`${pub.muted} max-w-xl mx-auto mb-6 text-sm md:text-base`}>{t("quote.funnel.desc")}</p>
                <button
                  type="button"
                  data-testid="quote-funnel-teklif-al"
                  onClick={() => {
                    document.getElementById("quote-contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    trackQuoteCTAClick({ language });
                  }}
                  className="inline-flex items-center justify-center gap-2 bg-[#FF4F00] hover:bg-[#E64700] text-black font-bold uppercase tracking-wider text-sm px-8 py-4 transition-all hover:-translate-y-0.5"
                >
                  {t("cta.getQuote")} <ArrowUpRight className="w-5 h-5" />
                </button>
              </div>
            ) : null}

            {/* ==== CONTACT + SUBMIT ==== */}
            <div id="quote-contact-form" data-testid="quote-contact-anchor">
              <div className="mb-6">
                <h2 className={`font-sans text-2xl md:text-3xl font-bold tracking-tight mb-2 ${pub.heroHeading}`}>{t("quote.title")}</h2>
                <p className={`font-sans text-sm ${pub.muted}`}>{t("quote.subtitle")}</p>
              </div>

              <div className={`grid grid-cols-1 md:grid-cols-2 rounded-sm overflow-hidden shadow-sm dark:shadow-none border ${pub.gridHairline} ${pub.borderSubtle}`}>
                <Field label={t("quote.field.fullName")}>
                  <input required value={form.name} onChange={(e) => updateField("name", e.target.value)} data-testid="quote-name-input-field" className={sansInputCls} placeholder={t("quote.placeholder.name")} />
                </Field>
                <Field label={t("quote.field.phone")}>
                  <input required value={form.phone} onChange={(e) => updateField("phone", e.target.value)} data-testid="quote-phone-input-field" className={sansInputCls} placeholder={t("quote.placeholder.phone")} />
                </Field>
                <Field label={t("quote.field.email")} span="md:col-span-2">
                  <input type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} data-testid="quote-email-input-field" className={sansInputCls} placeholder={t("quote.placeholder.email")} />
                </Field>
                <Field label={t("quote.field.quantity")} span="md:col-span-2">
                  <input
                    type="number"
                    min={1}
                    value={form.quantity >= 1 ? form.quantity : 1}
                    onChange={(e) => updateField("quantity", Math.max(1, parseInt(String(e.target.value), 10) || 1))}
                    data-testid="quote-quantity-input-field"
                    className={`max-w-[12rem] ${sansInputCls} tabular-nums`}
                  />
                </Field>
                <Field label={t("quote.field.notes")} span="md:col-span-2">
                  <textarea rows={5} value={form.notes} onChange={(e) => updateField("notes", e.target.value)} data-testid="quote-notes-input-field" className={sansInputCls + " h-auto py-3 min-h-[140px]"} placeholder={t("quote.placeholder.notes")} />
                </Field>
              </div>

              {submitError ? (
                <div role="alert" className={`rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm ${pub.heroHeading}`}>
                  {submitError}
                </div>
              ) : null}

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-button"
                  className="inline-flex items-center justify-between gap-2 bg-[#FF4F00] hover:bg-[#E64700] disabled:opacity-50 text-black font-bold uppercase tracking-wider text-sm px-6 py-5 transition-all hover:-translate-y-0.5"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> {t("quote.submit.sending")}
                    </>
                  ) : (
                    <>
                      {t("quote.submit")} <ArrowUpRight className="w-5 h-5" />
                    </>
                  )}
                </button>
                <a
                  href={siteConfig.whatsappLink(t("quote.whatsapp.intro"))}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="quote-whatsapp-fallback"
                  onClick={() => trackWhatsappClick("quote-fallback", { language })}
                  className={`inline-flex items-center justify-between gap-2 font-bold uppercase tracking-wider text-sm px-6 py-5 transition-colors ${pub.btnGhost}`}
                >
                  <MessageCircle className="w-5 h-5" /> {t("quote.whatsappSend")}
                </a>
              </div>
              <p className={`mt-4 font-sans text-xs leading-relaxed ${pub.mutedStr}`}>{t("quote.footerNote")}</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, span = "" }) {
  return (
    <label className={`bg-white dark:bg-[#0f0f14] p-5 block ${span}`}>
      <span className={`${pub.labelMuted} mb-2 block`}>{label}</span>
      {children}
    </label>
  );
}
